import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "./auth";

const SEEN_KEY = "affinage.tour.seen";
const INTRO_SEEN_KEY = "affinage.intro.seen";

type TourState = {
  isOpen: boolean;
  start: () => void;
  dismiss: () => void;
  markSeen: () => void;
  /** The one-time opening titles. Plays before the tour on a first run, and is
   *  replayable on demand from the same menu. */
  introOpen: boolean;
  startIntro: () => void;
  /** Ends the intro. `thenTour` is what a first run passes to hand straight
   *  over to the tour; a replay ends on its own. */
  dismissIntro: (thenTour?: boolean) => void;
};

const TourCtx = createContext<TourState | null>(null);

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string) {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // localStorage unavailable; ignore
  }
}

export function TourProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const { isNewUser, loading } = useAuth();

  const markSeen = useCallback(() => writeFlag(SEEN_KEY), []);

  const start = useCallback(() => {
    setIsOpen(true);
  }, []);

  const dismiss = useCallback(() => {
    setIsOpen(false);
    markSeen();
  }, [markSeen]);

  const startIntro = useCallback(() => {
    setIntroOpen(true);
  }, []);

  const dismissIntro = useCallback(
    (thenTour = false) => {
      setIntroOpen(false);
      writeFlag(INTRO_SEEN_KEY);
      if (thenTour) setIsOpen(true);
    },
    []
  );

  // First run: the titles, then the tour. Both are gated on their own flag, so
  // adding the intro later doesn't re-trigger the tour for someone who has
  // already taken it — and someone who skips the intro still gets offered it.
  //
  // The flags are localStorage, which makes them per-device: sign in on the web
  // and on a phone and the intro plays on each. Moving them to the user doc
  // would fix that at the cost of a Firestore read before the first screen,
  // which is exactly the kind of thing the load-time work removed. Per-device
  // is the cheaper wrong answer, and the wrongness is a few seconds, once.
  useEffect(() => {
    if (loading || !isNewUser) return;
    if (readFlag(INTRO_SEEN_KEY) || readFlag(SEEN_KEY)) return;
    // A beat, so the cookbook behind it has mounted and the curtain doesn't
    // rise on an empty frame.
    const t = window.setTimeout(() => setIntroOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [isNewUser, loading]);

  const value = useMemo<TourState>(
    () => ({ isOpen, start, dismiss, markSeen, introOpen, startIntro, dismissIntro }),
    [isOpen, start, dismiss, markSeen, introOpen, startIntro, dismissIntro]
  );

  return <TourCtx.Provider value={value}>{children}</TourCtx.Provider>;
}

export function useTour() {
  const ctx = useContext(TourCtx);
  if (!ctx) throw new Error("useTour must be used inside TourProvider");
  return ctx;
}
